import { useMemo, useRef, useState } from 'react'
import { Ban, Bookmark, Check, ChevronDown, ChevronRight, Focus, History, LayoutTemplate, Link2, Pencil, Plus, Search, Trash2, X, ZoomIn, ZoomOut } from 'lucide-react'
import { CARD_KINDS, LINK_KINDS, cardTintOf, kindById } from '../lib/canvas'
import { BUILTIN_FRAMEWORKS, frameworkById, frameworkSizeFor, layoutZones, nearestZoneAt, zoneAt } from '../lib/framework'
import { uid } from '../lib/utils'

function neighborIds(links, cardId) {
  const set = new Set([cardId])
  for (const link of links) {
    if (link.source === cardId) set.add(link.target)
    if (link.target === cardId) set.add(link.source)
  }
  return set
}

function relatedIds(links, cards, cardId) {
  const set = new Set([cardId])
  for (const link of links) {
    if (link.source === cardId) {
      const targetCard = cards.find((card) => card.id === link.target)
      if (targetCard && targetCard.kind === 'link') {
        set.add(link.target)
        for (const second of links) {
          if (second.source === link.target && second.target !== cardId) {
            set.add(second.target)
            const endCard = cards.find((card) => card.id === second.target)
            if (endCard && endCard.kind === 'link') {
              for (const third of links) { if (third.source === second.target && third.target !== link.target) set.add(third.target) }
            }
          }
        }
      } else {
        set.add(link.target)
      }
    }
    if (link.target === cardId) {
      const sourceCard = cards.find((card) => card.id === link.source)
      if (sourceCard && sourceCard.kind === 'link') {
        set.add(link.source)
        for (const second of links) {
          if (second.target === link.source && second.source !== cardId) {
            set.add(second.source)
            const startCard = cards.find((card) => card.id === second.source)
            if (startCard && startCard.kind === 'link') {
              for (const third of links) { if (third.target === second.source && third.source !== link.source) set.add(third.source) }
            }
          }
        }
      } else {
        set.add(link.source)
      }
    }
  }
  return set
}

function CardEditor({ card, links, onPatch, onDeleteLink, onClose, notify }) {
  const [title, setTitle] = useState(card.title || '')
  const [content, setContent] = useState(card.content || '')
  const [kind, setKind] = useState(card.kind || 'idea')
  const [color, setColor] = useState(card.color || kindById(card.kind).color)

  function save() {
    onPatch(card.id, { title, content, kind, color })
    onClose()
  }

  const related = links.filter((link) => link.source === card.id || link.target === card.id)

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="history-modal card-editor-modal" onClick={(event) => event.stopPropagation()}>
        <header>
          <div><h3>编辑卡片</h3><p>标题、内容、类型与颜色。</p></div>
          <button className="icon-button" onClick={onClose} aria-label="关闭"><X size={18} /></button>
        </header>
        <div className="card-editor-body">
          <input className="text-input" value={title} placeholder="卡片标题" aria-label="卡片标题" onChange={(event) => setTitle(event.target.value)} />
          {card.kind === 'link' ? <p className="card-editor-hint">关联原因：写一句话，或一整段、多行的解释都可以。</p> : null}
          <textarea rows={10} value={content} placeholder={card.kind === 'link' ? '为什么关联：写清楚原因（一句话或一整段多行内容）……' : '写下想法、摘录或情节……'} aria-label="卡片内容" onChange={(event) => setContent(event.target.value)} />
          <div className="card-editor-row">
            <label><span>类型</span>
              <select value={kind} onChange={(event) => { setKind(event.target.value); setColor(kindById(event.target.value).color) }} aria-label="卡片类型">
                {CARD_KINDS.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
              </select>
            </label>
            <label><span>颜色</span><input type="color" value={color} onChange={(event) => setColor(event.target.value)} aria-label="卡片颜色" /></label>
          </div>
          {related.length ? (
            <div className="card-editor-links">
              <span className="card-editor-links__head">关联线</span>
              {related.map((link) => (
                <div className="card-editor-link-row" key={link.id}>
                  <select value={link.label || '关联'} onChange={(event) => onPatch(null, { linkId: link.id, label: event.target.value })} aria-label="关联线类型">
                    {LINK_KINDS.map((label) => <option key={label} value={label}>{label}</option>)}
                  </select>
                  <button className="danger-link" onClick={() => onDeleteLink(link.id)} aria-label="删除关联线"><Trash2 size={13} /></button>
                </div>
              ))}
            </div>
          ) : null}
          <div className="library-actions">
            <button className="btn btn--ghost" onClick={onClose}>取消</button>
            <button className="btn btn--ink" onClick={save}>保存</button>
          </div>
        </div>
      </div>
    </div>
  )
}

function formatWhen(iso) {
  if (!iso) return ''
  const date = new Date(iso)
  const pad = (value) => String(value).padStart(2, '0')
  return pad(date.getMonth() + 1) + '-' + pad(date.getDate()) + ' ' + pad(date.getHours()) + ':' + pad(date.getMinutes())
}

function CardHistoryModal({ card, onRestore, onClose, notify }) {
  const history = card.history || []
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="history-modal card-history-modal" onClick={(event) => event.stopPropagation()}>
        <header>
          <div><h3>卡片历史 · 《{card.title || '未命名卡片'}》</h3><p>每次更新都是一条实时事件，可对照旧版本并恢复。</p></div>
          <button className="icon-button" onClick={onClose} aria-label="关闭"><X size={18} /></button>
        </header>
        <div className="card-history-body">
          <div className="card-history-current">
            <span className="card-history-current__tag">当前内容</span>
            <strong>{card.title || '未命名卡片'}</strong>
            <p>{card.content || '（空）'}</p>
          </div>
          {history.length ? (
            <div className="card-history-list">
              {history.map((entry, index) => (
                <div className="card-history-entry" key={index}>
                  <div className="card-history-entry__head">
                    <time>{formatWhen(entry.at)}</time>
                    <button className="mini-text-btn" onClick={() => { onRestore(entry); notify('已恢复到该版本。') }} aria-label="恢复到该版本">恢复到该版本</button>
                  </div>
                  <strong>{entry.title || '未命名卡片'}</strong>
                  <p>{entry.content ? entry.content.slice(0, 240) : '（空）'}</p>
                </div>
              ))}
            </div>
          ) : <p className="box-panel__empty">还没有历史事件。</p>}
        </div>
      </div>
    </div>
  )
}
function LinkPickerModal({ cards, sourceId, onCreate, onClose }) {
  const [query, setQuery] = useState('')
  const [kindFilter, setKindFilter] = useState('')
  const sourceCard = cards.find((card) => card.id === sourceId)
  const candidates = cards.filter((card) => card.id !== sourceId && card.status !== 'trash' && card.status !== 'inbook' && (!kindFilter || card.kind === kindFilter) && (!query || ((card.title || '') + (card.content || '')).includes(query)))
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="history-modal link-picker-modal" onClick={(event) => event.stopPropagation()}>
        <header>
          <div><h3>筛选关联</h3><p>选择要连接到《{sourceCard ? (sourceCard.title || '未命名') : '卡片'}》的卡片。</p></div>
          <button className="icon-button" onClick={onClose} aria-label="关闭"><X size={18} /></button>
        </header>
        <div className="link-picker-body">
          <input className="text-input" value={query} placeholder="搜索卡片标题或内容…" aria-label="搜索卡片" onChange={(event) => setQuery(event.target.value)} />
          <div className="link-picker-filters">
            <button className={'filter-chip ' + (kindFilter === '' ? 'is-active' : '')} onClick={() => setKindFilter('')}>全部</button>
            {CARD_KINDS.map((item) => (
              <button key={item.id} className={'filter-chip ' + (kindFilter === item.id ? 'is-active' : '')} onClick={() => setKindFilter(kindFilter === item.id ? '' : item.id)}>{item.label}</button>
            ))}
          </div>
          <div className="link-picker-list">
            {candidates.map((card) => (
              <button key={card.id} className="link-pick-row" onClick={() => onCreate(card.id)}>
                <span className="canvas-card__kind" style={{ background: cardTintOf(card) }}>{kindById(card.kind).label}</span>
                <strong>{card.title || '未命名卡片'}</strong>
                <p>{card.content ? card.content.slice(0, 60) : ''}</p>
              </button>
            ))}
            {candidates.length === 0 ? <p className="box-panel__empty">没有符合条件的卡片。</p> : null}
          </div>
        </div>
      </div>
    </div>
  )
}
function FrameworkModal({ frameworks, activeId, onSet, onClear, onCreateFramework, onClose }) {
  const [builderOpen, setBuilderOpen] = useState(false)
  const [customName, setCustomName] = useState('')
  const [customZones, setCustomZones] = useState('')
  function saveCustom() {
    const zones = customZones.split('\n').map((line) => line.trim()).filter(Boolean)
    if (!customName.trim() || !zones.length) return
    onCreateFramework(customName.trim(), zones)
    setCustomName('')
    setCustomZones('')
    setBuilderOpen(false)
  }
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="history-modal framework-modal" onClick={(event) => event.stopPropagation()}>
        <header>
          <div><h3>思考框架</h3><p>框架是画板背景：设好后把卡片拖进区域归类；背景范围会随卡片数量自动扩展。</p></div>
          <button className="icon-button" onClick={onClose} aria-label="关闭"><X size={18} /></button>
        </header>
        <div className="framework-body">
          {activeId ? (
            <button className="framework-add framework-add--clear" onClick={onClear} aria-label="清除背景">✕ 清除当前背景（{frameworks.find((framework) => framework.id === activeId)?.name || activeId}）</button>
          ) : null}
          {frameworks.map((framework) => (
            <div className="framework-row" key={framework.id}>
              <div>
                <strong>{framework.name}</strong>
                <small>{framework.kind === 'pyramid' ? '金字塔分层' : '分列区域'} · {(framework.zones || []).map((zone) => zone.label).join(' / ')}</small>
              </div>
              {activeId === framework.id ? (
                <span className="framework-active-tag">当前背景</span>
              ) : (
                <button className="btn btn--ink btn--small" onClick={() => onSet(framework)} aria-label="设为背景">设为背景</button>
              )}
            </div>
          ))}
          {builderOpen ? (
            <div className="framework-builder">
              <input className="text-input" value={customName} placeholder="框架名称" aria-label="框架名称" onChange={(event) => setCustomName(event.target.value)} />
              <textarea rows={3} value={customZones} placeholder="每行一个区域，例如：&#10;第一幕&#10;第二幕&#10;第三幕" aria-label="层列表" onChange={(event) => setCustomZones(event.target.value)} />
              <div className="library-actions">
                <button className="btn btn--ghost" onClick={() => setBuilderOpen(false)}>取消</button>
                <button className="btn btn--ink" onClick={saveCustom} aria-label="保存自定义框架">保存框架</button>
              </div>
            </div>
          ) : (
            <button className="framework-add" onClick={() => setBuilderOpen(true)} aria-label="新建框架">+ 新建自定义框架</button>
          )}
        </div>
      </div>
    </div>
  )
}
function ConnectModeModal({ sourceCard, targetCard, onDirect, onTriad, onClose }) {
  const [note, setNote] = useState('')
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="history-modal connect-mode-modal" onClick={(event) => event.stopPropagation()}>
        <header>
          <div><h3>建立关联</h3><p>从《{sourceCard ? (sourceCard.title || '未命名') : '卡片'}》连向《{targetCard ? (targetCard.title || '未命名') : '卡片'}》，选择关联线模式。</p></div>
          <button className="icon-button" onClick={onClose} aria-label="关闭"><X size={18} /></button>
        </header>
        <div className="connect-mode-body">
          <div className="connect-mode-option">
            <div className="connect-mode-option__head"><strong>单独的单向线</strong><em>轻量模式</em></div>
            <p>一条箭头线直接连接两张卡，原因写一句话附在线上。</p>
            <input className="text-input" value={note} placeholder="一句话原因（必填）…" aria-label="一句话原因" onChange={(event) => setNote(event.target.value)} />
            <button className="btn btn--ink btn--small" onClick={() => onDirect(note)} aria-label="直接连线">直接连线</button>
          </div>
          <div className="connect-mode-option">
            <div className="connect-mode-option__head"><strong>添加卡片的单向线</strong><em>解释模式</em></div>
            <p>中间生成一张「关联原因」卡，两条单向线相连，原因可以写成整张解释卡。</p>
            <button className="btn btn--ink btn--small" onClick={onTriad} aria-label="添加关联卡连线">添加关联卡连线</button>
          </div>
        </div>
      </div>
    </div>
  )
}
export default function CanvasView({ canvas, onChange, onOpenRoundtable, notify }) {
  const canvasRef = useRef(null)
  const [pan, setPan] = useState({ x: 60, y: 40 })
  const [zoom, setZoom] = useState(1)
  const [panning, setPanning] = useState(false)
  const [connectFrom, setConnectFrom] = useState(null)
  const [focusedId, setFocusedId] = useState(null)
  const [collapsed, setCollapsed] = useState(() => (canvas.view && canvas.view.collapsed) || {})

  function toggleCollapsed(id) {
    const next = { ...collapsed, [id]: !collapsed[id] }
    setCollapsed(next)
    commit({ ...canvas, view: { ...(canvas.view || {}), collapsed: next } })
  }
  const [selected, setSelected] = useState(() => new Set())
  const [editingId, setEditingId] = useState(null)
  const [dragCard, setDragCard] = useState(null)
  const [openMap, setOpenMap] = useState({})
  const [selectedLinkId, setSelectedLinkId] = useState(null)
  const [activeCardId, setActiveCardId] = useState(null)
  const [historyOpenId, setHistoryOpenId] = useState(null)
  const [drillStack, setDrillStack] = useState([])
  const [pickerFromId, setPickerFromId] = useState(null)
  const [frameworkOpen, setFrameworkOpen] = useState(false)
  const [pendingConnect, setPendingConnect] = useState(null)
  const panRef = useRef(null)
  const pendingOpenRef = useRef(null)

  function toggleOpen(id) {
    setOpenMap((current) => {
      const next = { ...current }
      if (next[id]) delete next[id]
      else next[id] = 1.4
      return next
    })
  }

  function zoomOpen(id, delta) {
    setOpenMap((current) => {
      const currentScale = current[id] || 1.4
      const nextScale = Math.round(Math.min(3, Math.max(0.8, currentScale + delta)) * 10) / 10
      return { ...current, [id]: nextScale }
    })
  }

  const cards = canvas.cards || []
  const links = canvas.links || []

  const childMap = useMemo(() => {
    const map = new Map()
    for (const link of links) {
      if (!map.has(link.source)) map.set(link.source, [])
      map.get(link.source).push(link.target)
    }
    return map
  }, [links])

  const hiddenSet = useMemo(() => {
    const hidden = new Set()
    const roots = new Set(Object.keys(collapsed).filter((id) => collapsed[id]))
    const stack = []
    for (const root of roots) stack.push(...(childMap.get(root) || []))
    while (stack.length) {
      const id = stack.pop()
      if (hidden.has(id) || roots.has(id)) continue
      hidden.add(id)
      stack.push(...(childMap.get(id) || []))
    }
    return hidden
  }, [collapsed, childMap])

  const focusSet = focusedId ? relatedIds(links, cards, focusedId) : null
  const activeSet = activeCardId ? relatedIds(links, cards, activeCardId) : null
  const userFrameworks = canvas.frameworks || []
  const activeFramework = frameworkById(canvas.frameworkId, userFrameworks)
  const frameworkSize = useMemo(() => frameworkSizeFor(cards.filter((card) => card.status !== 'trash' && card.status !== 'inbook')), [cards])

  const drillVisibleSet = useMemo(() => {
    if (!drillStack.length) return null
    const top = drillStack[drillStack.length - 1]
    const seen = new Set([top])
    const stack = [top]
    while (stack.length) {
      const id = stack.pop()
      for (const child of (childMap.get(id) || [])) {
        if (seen.has(child)) continue
        seen.add(child)
        stack.push(child)
      }
    }
    return seen
  }, [drillStack, childMap])

  const visibleCards = cards.filter((card) => {
    if (card.status === 'trash' || card.status === 'inbook') return false
    if (hiddenSet.has(card.id)) return false
    if (focusSet && !focusSet.has(card.id)) return false
    if (drillVisibleSet && !drillVisibleSet.has(card.id)) return false
    return true
  })

  const frameworkRenderList = useMemo(() => {
    const list = []
    const seen = new Set()
    if (activeFramework) { list.push(activeFramework); seen.add(activeFramework.id) }
    for (const card of visibleCards) {
      if (!openMap[card.id] || !card.frameworkId || seen.has(card.frameworkId)) continue
      const framework = frameworkById(card.frameworkId, userFrameworks)
      if (framework) { list.push(framework); seen.add(framework.id) }
    }
    return list
  }, [activeFramework, visibleCards, openMap, userFrameworks])

  function commit(next) { onChange(next) }

  function patchCard(id, patch) {
    commit({
      ...canvas,
      cards: cards.map((card) => {
        if (card.id !== id) return card
        const meaningful = Object.keys(patch).some((key) => key !== 'x' && key !== 'y' && key !== 'order')
        const history = meaningful
          ? [{ at: new Date().toISOString(), title: card.title, content: card.content, kind: card.kind, color: card.color }, ...(card.history || [])].slice(0, 30)
          : (card.history || [])
        return { ...card, ...patch, history, updatedAt: new Date().toISOString() }
      }),
    })
  }

  function addCard(x, y) {
    const card = {
      id: uid('card'),
      kind: 'idea',
      title: '',
      content: '',
      color: kindById('idea').color,
      x: Math.round(x),
      y: Math.round(y),
      status: 'active',
      column: 'inspiration',
      act: '未分幕',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    const topId = drillStack.length ? drillStack[drillStack.length - 1] : null
    const extraLinks = topId ? [{ id: uid('link'), source: topId, target: card.id, label: '延伸', directed: true }] : []
    commit({ ...canvas, cards: [...cards, card], links: [...links, ...extraLinks] })
    setEditingId(card.id)
  }

  function setFrameworkBg(framework) {
    commit({ ...canvas, frameworkId: framework.id })
    setFrameworkOpen(false)
    notify('「' + framework.name + '」已设为画板背景，卡片拖入区域即可归类。')
  }

  function clearFrameworkBg() {
    commit({ ...canvas, frameworkId: null })
    setFrameworkOpen(false)
    notify('已清除框架背景。')
  }

  function createFramework(name, zoneLabels) {
    const framework = {
      id: 'fw-user-' + uid('fw'),
      name,
      kind: 'columns',
      zones: zoneLabels.map((label) => ({ id: 'z-' + uid('z'), label, color: '#c8b27e', items: [] })),
    }
    commit({ ...canvas, frameworks: [...(canvas.frameworks || []), framework] })
    notify('自定义框架「' + name + '」已存入框架仓库。')
  }

  function spreadChildren(card) {
    const kids = (childMap.get(card.id) || []).filter((id) => id !== card.id)
    if (!kids.length) { notify('这张卡片还没有子级。'); return }
    const radius = Math.max(340, kids.length * 90)
    const start = -Math.PI / 2 - (kids.length - 1) * 0.35
    const step = kids.length > 1 ? 0.7 : 0.5
    const positions = {}
    kids.forEach((kidId, index) => {
      const angle = start + index * step
      positions[kidId] = { x: Math.round(card.x + Math.cos(angle) * radius - 110), y: Math.round(card.y + Math.sin(angle) * radius - 50) }
    })
    commit({ ...canvas, cards: cards.map((item) => positions[item.id] ? { ...item, x: positions[item.id].x, y: positions[item.id].y } : item) })
    notify('已铺开 ' + kids.length + ' 张子级卡片。')
  }

  function toWorld(clientX, clientY) {
    const rect = canvasRef.current ? canvasRef.current.getBoundingClientRect() : { left: 0, top: 0 }
    return { x: (clientX - rect.left - pan.x) / zoom, y: (clientY - rect.top - pan.y) / zoom }
  }

  function startPan(event) {
    if (event.button !== 0) return
    if (event.target.closest && event.target.closest('.canvas-card')) return
    panRef.current = { startX: event.clientX, startY: event.clientY, originX: pan.x, originY: pan.y }
    setPanning(true)
  }

  function movePan(event) {
    const ref = panRef.current
    if (!ref) return
    setPan({ x: ref.originX + (event.clientX - ref.startX), y: ref.originY + (event.clientY - ref.startY) })
  }

  function endPan() { panRef.current = null; setPanning(false) }

  function startCardDrag(event, card) {
    event.stopPropagation()
    if (event.button !== 0) return
    if (event.currentTarget && typeof event.currentTarget.setPointerCapture === 'function') {
      try { event.currentTarget.setPointerCapture(event.pointerId) } catch (error) { /* ignore */ }
    }
    setDragCard({ id: card.id, startX: event.clientX, startY: event.clientY, originX: card.x, originY: card.y })
  }

  function moveCardDrag(event) {
    const drag = dragCard
    if (!drag) return
    const dx = (event.clientX - drag.startX) / zoom
    const dy = (event.clientY - drag.startY) / zoom
    patchCard(drag.id, { x: Math.round(drag.originX + dx), y: Math.round(drag.originY + dy) })
  }

  function endCardDrag() {
    const drag = dragCard
    setDragCard(null)
    if (!drag || !activeFramework) return
    const card = cards.find((item) => item.id === drag.id)
    if (!card) return
    const zone = zoneAt(activeFramework, card.x + 110, card.y + 70, frameworkSize) || nearestZoneAt(activeFramework, card.x + 110, card.y + 70, frameworkSize)
    if (zone && zone.id !== card.zoneId) {
      patchCard(card.id, { zoneId: zone.id, frameworkId: activeFramework.id })
      notify('已放入「' + zone.label + '」区域。')
    }
  }

  function createDirectLink(sourceId, targetId, note) {
    if (sourceId === targetId) { notify('不能连向自己。'); return }
    commit({ ...canvas, links: [...links, { id: uid('link'), source: sourceId, target: targetId, label: '关联', directed: true, note: note || '' }] })
    notify('已建立单独的单向关联线。')
  }

  function createLinkBetween(sourceId, targetId) {
    if (sourceId === targetId) { notify('不能连向自己。'); return }
    const source = cards.find((card) => card.id === sourceId)
    const target = cards.find((card) => card.id === targetId)
    if (!source || !target) return
    const now = new Date().toISOString()
    const linkCard = {
      id: uid('card'),
      kind: 'link',
      title: '关联原因',
      content: '为什么关联：',
      color: kindById('link').color,
      x: Math.round((source.x + target.x) / 2),
      y: Math.round((source.y + target.y) / 2),
      status: 'active',
      column: 'inspiration',
      act: '未分幕',
      createdAt: now,
      updatedAt: now,
    }
    const lineA = { id: uid('link'), source: sourceId, target: linkCard.id, label: '关联', directed: true }
    const lineB = { id: uid('link'), source: linkCard.id, target: targetId, label: '关联', directed: true }
    commit({ ...canvas, cards: [...cards, linkCard], links: [...links, lineA, lineB] })
    setEditingId(linkCard.id)
    notify('已建立关联：请写下一句关联原因，或写成一整张解释卡。')
  }

  function findMiddleCard(link) {
    if (!link) return null
    const candidate = cards.find((card) => card.kind === 'link' && links.some((first) => first.source === link.source && first.target === card.id) && links.some((second) => second.source === card.id && second.target === link.target))
    if (candidate) return candidate
    return cards.find((card) => card.id === link.target && card.kind === 'link') || null
  }

  function deleteTriad(link) {
    const middle = findMiddleCard(link)
    if (middle) {
      commit({ ...canvas, cards: cards.filter((card) => card.id !== middle.id), links: links.filter((item) => item.source !== middle.id && item.target !== middle.id) })
      notify('已取消这条关联。')
    } else {
      commit({ ...canvas, links: links.filter((item) => item.id !== link.id) })
      notify('已取消这条关联。')
    }
    setSelectedLinkId(null)
  }

  function editLinkReason(link) {
    const middle = findMiddleCard(link)
    if (middle) {
      setSelectedLinkId(null)
      setEditingId(middle.id)
      notify('请写下关联原因（一句话或一整张解释卡）。')
    } else {
      notify('未找到关联原因卡。')
    }
  }

  function handleCardClick(event, card) {
    if (dragCard) return
    if (connectFrom) {
      if (connectFrom === card.id) { setConnectFrom(null); notify('不能连向自己。'); return }
      setPendingConnect({ source: connectFrom, target: card.id })
      setConnectFrom(null)
      return
    }
    setActiveCardId(card.id)
    if (pendingOpenRef.current) { window.clearTimeout(pendingOpenRef.current.timer); pendingOpenRef.current = null }
    pendingOpenRef.current = { id: card.id, timer: window.setTimeout(() => { toggleOpen(card.id); pendingOpenRef.current = null }, 260) }
  }

  function handleCardDoubleClick(event, card) {
    event.stopPropagation()
    if (event.target.closest && event.target.closest('button')) return
    if (pendingOpenRef.current && pendingOpenRef.current.id === card.id) {
      window.clearTimeout(pendingOpenRef.current.timer)
      pendingOpenRef.current = null
    }
    setEditingId(card.id)
  }

  function spawnExplain(link) {
    const source = cards.find((card) => card.id === link.source)
    const target = cards.find((card) => card.id === link.target)
    if (!source || !target) return
    const card = {
      id: uid('card'),
      kind: 'idea',
      title: '关联解释',
      content: '解释「' + (source.title || '未命名') + '」与「' + (target.title || '未命名') + '」的关系：',
      color: kindById('idea').color,
      x: Math.round((source.x + target.x) / 2),
      y: Math.round((source.y + target.y) / 2),
      status: 'active',
      column: 'inspiration',
      act: '未分幕',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    commit({ ...canvas, cards: [...cards, card] })
    setSelectedLinkId(null)
    setEditingId(card.id)
    notify('已生成关联解释卡，请补充说明。')
  }

  function toggleSelect(id) {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectedCards = cards.filter((card) => selected.has(card.id) && card.status !== 'trash')
  const selectedLink = selectedLinkId ? links.find((link) => link.id === selectedLinkId) : null

  return (
    <div className="canvas-view">
      <div className="canvas-toolbar">
        <div className="canvas-toolbar__title"><Focus size={15} /> 关联画板 <em>无边画板 · 便签与关联线</em></div>
        {drillStack.length ? (
          <div className="canvas-drill">
            <button className="canvas-mini" onClick={() => setDrillStack([])} aria-label="返回画板">画板</button>
            {drillStack.map((id) => {
              const crumbCard = cards.find((card) => card.id === id)
              return (
                <span key={id} className="canvas-drill__group">
                  <span className="canvas-drill__sep">/</span>
                  <button className="canvas-mini" onClick={() => setDrillStack(drillStack.slice(0, drillStack.indexOf(id) + 1))}>{crumbCard ? (crumbCard.title || '未命名') : '?'}</button>
                </span>
              )
            })}
            <em>次层级查看模式</em>
          </div>
        ) : null}
        <div className="canvas-toolbar__tools">
          <button className="btn btn--ink" onClick={() => addCard(120 - pan.x, 80 - pan.y)} aria-label="新建卡片"><Plus size={14} /> 新建卡片</button>
          <button className="btn btn--outline" onClick={() => setFrameworkOpen(true)} aria-label="框架"><LayoutTemplate size={14} /> 框架</button>
          {connectFrom ? <span className="canvas-hint">选择目标卡片建立关联… <button className="mini-text-btn" onClick={() => setConnectFrom(null)}>取消</button></span> : null}
          {focusedId ? <button className="btn btn--outline" onClick={() => setFocusedId(null)} aria-label="取消聚焦"><Ban size={13} /> 取消聚焦</button> : null}
          <div className="zoom-controls">
            <button className="icon-btn" onClick={() => setZoom((z) => Math.min(2, z + 0.1))} aria-label="放大"><ZoomIn size={14} /></button>
            <span>{Math.round(zoom * 100)}%</span>
            <button className="icon-btn" onClick={() => setZoom((z) => Math.max(0.4, z - 0.1))} aria-label="缩小"><ZoomOut size={14} /></button>
          </div>
          <button className="btn btn--outline" disabled={!selectedCards.length} onClick={() => onOpenRoundtable([...selected])} aria-label="带勾选卡片去圆桌">
            带 {selectedCards.length} 张去圆桌
          </button>
          {selectedLink ? (
            <div className="canvas-linkpanel">
              <span>关联线</span>
              <select value={selectedLink.label || '关联'} onChange={(event) => commit({ ...canvas, links: links.map((link) => link.id === selectedLink.id ? { ...link, label: event.target.value } : link) })} aria-label="关联线类型">
                {LINK_KINDS.map((label) => <option key={label} value={label}>{label}</option>)}
              </select>
              <span className="canvas-mini is-on">单向</span>
              {findMiddleCard(selectedLink) ? (
                <button className="canvas-mini" onClick={() => editLinkReason(selectedLink)} aria-label="编辑关联原因">编辑关联原因</button>
              ) : (
                <input className="canvas-linkpanel__note" value={selectedLink.note || ''} placeholder="一句话原因…" aria-label="一句话原因" onChange={(event) => commit({ ...canvas, links: links.map((link) => link.id === selectedLink.id ? { ...link, note: event.target.value } : link) })} />
              )}
              <button className="canvas-mini" onClick={() => { if (findMiddleCard(selectedLink)) deleteTriad(selectedLink); else { commit({ ...canvas, links: links.filter((link) => link.id !== selectedLink.id) }); setSelectedLinkId(null); notify('已取消这条关联。') } }} aria-label="取消关联">取消关联</button>
              <button className="canvas-mini" onClick={() => setSelectedLinkId(null)} aria-label="关闭关联线面板">×</button>
            </div>
          ) : null}
        </div>
      </div>

      <div
        className={'canvas-canvas ' + (panning ? 'is-panning' : '')}
        ref={canvasRef}
        onPointerDown={startPan}
        onPointerMove={movePan}
        onPointerUp={endPan}
        onPointerCancel={endPan}
        onWheel={(event) => { event.preventDefault(); setZoom((z) => Math.min(2, Math.max(0.4, z + (event.deltaY < 0 ? 0.1 : -0.1)))) }}
        onDoubleClick={(event) => { if (!event.target.closest('.canvas-card')) { const world = toWorld(event.clientX, event.clientY); addCard(world.x - 110, world.y - 40) } }}
        onClick={(event) => { if (!event.target.closest('.canvas-card') && !event.target.closest('.canvas-links')) setActiveCardId(null) }}
      >
        <svg className="canvas-links" aria-label="关联线画布">
          <defs>
            <marker id="canvas-arrow" viewBox="0 0 10 10" refX="8.5" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#4d6b52" />
            </marker>
          </defs>
          {links.filter((link) => {
            const source = cards.find((card) => card.id === link.source)
            const target = cards.find((card) => card.id === link.target)
            if (!source || !target) return false
            if (source.status === 'trash' || target.status === 'trash' || source.status === 'inbook' || target.status === 'inbook') return false
            if (focusSet && (!focusSet.has(link.source) || !focusSet.has(link.target))) return false
            if (drillVisibleSet && (!drillVisibleSet.has(link.source) || !drillVisibleSet.has(link.target))) return false
            return !hiddenSet.has(link.source) && !hiddenSet.has(link.target)
          }).map((link) => {
            const source = cards.find((card) => card.id === link.source)
            const target = cards.find((card) => card.id === link.target)
            const c1x = (source.x + 110) * zoom + pan.x
            const c1y = (source.y + 50) * zoom + pan.y
            const c2x = (target.x + 110) * zoom + pan.x
            const c2y = (target.y + 50) * zoom + pan.y
            const dx = c2x - c1x
            const dy = c2y - c1y
            const len = Math.sqrt(dx * dx + dy * dy) || 1
            const ux = dx / len
            const uy = dy / len
            const edgeDist = Math.min(110 / Math.max(Math.abs(ux), 0.01), 50 / Math.max(Math.abs(uy), 0.01))
            const x1 = c1x + ux * (edgeDist + 8)
            const y1 = c1y + uy * (edgeDist + 8)
            const x2 = c2x - ux * (edgeDist + 16)
            const y2 = c2y - uy * (edgeDist + 16)
            const midX = (c1x + c2x) / 2
            const midY = (c1y + c2y) / 2
            const touchesActive = Boolean(activeCardId && activeSet && (activeSet.has(link.source) || activeSet.has(link.target)))
            const dimmed = Boolean(activeCardId && !touchesActive)
            return (
              <g key={link.id} className="canvas-link-group" onPointerDown={(event) => event.stopPropagation()} onClick={(event) => { event.stopPropagation(); setSelectedLinkId(link.id) }}>
                <line x1={x1} y1={y1} x2={x2} y2={y2} className={'canvas-link canvas-link--' + (link.label || '关联') + (touchesActive ? ' is-active' : '') + (dimmed ? ' is-dimmed' : '')} markerEnd="url(#canvas-arrow)" />
                <text x={midX} y={midY - 6} className={'canvas-link-label' + (dimmed ? ' is-dimmed' : '')} fontSize={10 * zoom}>{link.label || '关联'}</text>
              </g>
            )
          })}
        </svg>

        <div className="canvas-world" style={{ transform: 'translate(' + pan.x + 'px, ' + pan.y + 'px) scale(' + zoom + ')' }}>
          {frameworkRenderList.map((framework) => (
            <div key={framework.id} className="canvas-framework" style={{ width: frameworkSize.w, height: frameworkSize.h }} aria-label="框架背景">
              {layoutZones(framework).map((zone) => {
                const zoneActive = visibleCards.some((card) => openMap[card.id] && card.frameworkId === framework.id && card.zoneId === zone.id)
                return (
                  <div key={zone.id} className={'canvas-zone' + (zoneActive ? ' is-zone-active' : '')} style={{ left: zone.x + '%', top: zone.y + '%', width: zone.w + '%', height: zone.h + '%', borderColor: zone.color }}>
                    <span style={{ background: zone.color }}>{zone.label}</span>
                    {zone.items && zone.items.length ? (
                      <div className="canvas-zone__items">
                        {zone.items.map((item) => <em key={item}>{item}</em>)}
                      </div>
                    ) : null}
                  </div>
                )
              })}
            </div>
          ))}
          {visibleCards.map((card) => {
            const childrenCount = (childMap.get(card.id) || []).length
            const isCollected = card.status === 'collected'
            const isFocused = focusedId === card.id
            const isCollapsed = Boolean(collapsed[card.id])
            const isSelected = selected.has(card.id)
            const isOpen = Boolean(openMap[card.id])
            const openScale = openMap[card.id] || 1
            const isRelated = activeCardId && activeSet && activeSet.has(card.id) && card.id !== activeCardId
            const isDimmed = Boolean(activeCardId && (!activeSet || !activeSet.has(card.id)) && !isOpen)
            return (
              <div
                key={card.id}
                className={['canvas-card', isCollected ? 'is-collected' : '', isFocused ? 'is-focused' : '', isSelected ? 'is-selected' : '', isOpen ? 'is-open' : '', isRelated ? 'is-related' : '', isDimmed ? 'is-dimmed' : '', connectFrom ? 'is-connectable' : ''].join(' ')}
                style={{ left: card.x + 'px', top: card.y + 'px', background: cardTintOf(card), transform: isOpen ? 'scale(' + openScale + ')' : undefined, zIndex: isOpen ? 30 : undefined }}
                onPointerDown={(event) => startCardDrag(event, card)}
                onPointerMove={moveCardDrag}
                onPointerUp={endCardDrag}
                onPointerCancel={endCardDrag}
                onClick={(event) => handleCardClick(event, card)}
                onDoubleClick={(event) => handleCardDoubleClick(event, card)}
              >
                <div className="canvas-card__top">
                  <button className={'canvas-check ' + (isSelected ? 'is-checked' : '')} onPointerDown={(event) => event.stopPropagation()} onClick={(event) => { event.stopPropagation(); toggleSelect(card.id) }} aria-label="勾选卡片"><Check size={10} /></button>
                  <span className="canvas-card__kind">{kindById(card.kind).label}</span>
                  {isCollected ? <span className="canvas-card__badge">收纳筐</span> : null}
                  <span className="canvas-card__when">{formatWhen(card.updatedAt)}</span>
                  {isOpen ? <span className="canvas-card__badge canvas-card__badge--open">已展开 ×{openScale.toFixed(1)}</span> : null}
                  <button className="canvas-mini" onPointerDown={(event) => event.stopPropagation()} onClick={(event) => { event.stopPropagation(); if (childrenCount === 0) { notify('这张卡片还没有子级。'); return } toggleCollapsed(card.id) }} aria-label="展开收起">
                    {isCollapsed ? <ChevronRight size={10} /> : <ChevronDown size={10} />}{isCollapsed ? '展开' : '收起'} {childrenCount}
                  </button>
                </div>
                <div className="canvas-card__body">
                  <h3>{card.title || '未命名卡片'}</h3>
                  {isOpen ? <p>{card.content || '双击编辑内容…'}</p> : <p>{card.content ? card.content.slice(0, 90) : '双击编辑内容…'}</p>}
                </div>
                <div className="canvas-card__foot" onPointerDown={(event) => event.stopPropagation()}>
                  {isOpen ? (
                    <span className="canvas-zoomset">
                      <button className="canvas-mini" onClick={(event) => { event.stopPropagation(); zoomOpen(card.id, 0.2) }} aria-label="放大卡片"><ZoomIn size={10} /></button>
                      <button className="canvas-mini" onClick={(event) => { event.stopPropagation(); zoomOpen(card.id, -0.2) }} aria-label="缩小卡片"><ZoomOut size={10} /></button>
                    </span>
                  ) : null}
                  <button className="canvas-mini" onClick={(event) => { event.stopPropagation(); setEditingId(card.id) }} aria-label="编辑卡片"><Pencil size={10} /> 编辑</button>
                  <button className="canvas-mini" onClick={(event) => { event.stopPropagation(); setConnectFrom(card.id) }} aria-label="连线"><Link2 size={10} /> 连线</button>
                  <button className="canvas-mini" onClick={(event) => { event.stopPropagation(); setPickerFromId(card.id) }} aria-label="筛选关联"><Search size={10} /> 筛选</button>
                  {childrenCount > 0 ? (
                    <button className="canvas-mini" onClick={(event) => { event.stopPropagation(); spreadChildren(card) }} aria-label="铺开子级">铺开</button>
                  ) : null}
                  <button className="canvas-mini" onClick={(event) => { event.stopPropagation(); setDrillStack((stack) => [...stack, card.id]); setActiveCardId(null) }} aria-label="进入次层级">进入</button>
                  <button className="canvas-mini" onClick={(event) => { event.stopPropagation(); setFocusedId(isFocused ? null : card.id) }} aria-label="聚焦"><Focus size={10} /> {isFocused ? '取消聚焦' : '聚焦'}</button>
                  <button className="canvas-mini" onClick={(event) => { event.stopPropagation(); patchCard(card.id, { status: isCollected ? 'active' : 'collected' }) }} aria-label="收纳"><Bookmark size={10} /> {isCollected ? '移出' : '收纳'}</button>
                  <button className="canvas-mini" onClick={(event) => { event.stopPropagation(); patchCard(card.id, { status: 'trash' }) }} aria-label="丢入回收箱"><Trash2 size={10} /></button>
                  {card.history && card.history.length > 0 ? (
                    <button className="canvas-mini canvas-mini--history" onClick={(event) => { event.stopPropagation(); setHistoryOpenId(card.id) }} aria-label="查看历史"><History size={10} /> 历史</button>
                  ) : null}
                </div>
              </div>
            )
          })}

          {visibleCards.length === 0 && cards.length === 0 ? (
            <div className="canvas-empty"><p>双击画板任意位置贴一张便签，或点左上角「新建卡片」。</p></div>
          ) : null}
        </div>
      </div>

      {pendingConnect ? (
        <ConnectModeModal
          sourceCard={cards.find((card) => card.id === pendingConnect.source)}
          targetCard={cards.find((card) => card.id === pendingConnect.target)}
          onDirect={(note) => {
            if (!note.trim()) { notify('请填写一句话原因。'); return }
            createDirectLink(pendingConnect.source, pendingConnect.target, note.trim())
            setPendingConnect(null)
          }}
          onTriad={() => {
            createLinkBetween(pendingConnect.source, pendingConnect.target)
            setPendingConnect(null)
          }}
          onClose={() => setPendingConnect(null)}
        />
      ) : null}

      {frameworkOpen ? (
        <FrameworkModal
          frameworks={[...BUILTIN_FRAMEWORKS, ...(canvas.frameworks || [])]}
          activeId={canvas.frameworkId || null}
          onSet={setFrameworkBg}
          onClear={clearFrameworkBg}
          onCreateFramework={createFramework}
          onClose={() => setFrameworkOpen(false)}
        />
      ) : null}

      {pickerFromId ? (
        <LinkPickerModal
          cards={cards}
          sourceId={pickerFromId}
          onCreate={(targetId) => {
            createLinkBetween(pickerFromId, targetId)
            setPickerFromId(null)
          }}
          onClose={() => setPickerFromId(null)}
        />
      ) : null}

      {historyOpenId ? (
        <CardHistoryModal
          card={cards.find((card) => card.id === historyOpenId) || { id: historyOpenId, title: '', content: '', history: [] }}
          onRestore={(entry) => patchCard(historyOpenId, { title: entry.title, content: entry.content })}
          onClose={() => setHistoryOpenId(null)}
          notify={notify}
        />
      ) : null}

      {editingId ? (
        <CardEditor
          card={cards.find((card) => card.id === editingId) || { id: editingId, title: '', content: '', kind: 'idea', color: kindById('idea').color }}
          links={links}
          onPatch={(id, patch) => {
            if (patch && patch.linkId) {
              commit({ ...canvas, links: links.map((link) => link.id === patch.linkId ? { ...link, label: patch.label } : link) })
            } else {
              patchCard(id, patch)
            }
          }}
          onDeleteLink={(linkId) => commit({ ...canvas, links: links.filter((link) => link.id !== linkId) })}
          onClose={() => setEditingId(null)}
          notify={notify}
        />
      ) : null}
    </div>
  )
}

